import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NotePencil, CheckSquare, CalendarBlank, ArrowsIn, ArrowsOut, SignOut, CaretDown, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTaskReminders } from "../hooks/useTaskReminders";
import { useAuth } from "../context/AuthContext";
import { OfflineStatusBadge } from "./OfflineStatusBadge";
import { GlobalSearchBox } from "./GlobalSearchBox";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "./ui/alert-dialog";

// Persistent top-level switcher between the app's 3 independent modules -
// Notes (screenshots/text), Tasks (simple to-dos), and Schedule (a calendar
// view of the same tasks, grouped by due date). Rendered at the top of every
// page in all 3 modules, desktop and mobile alike. Also self-fetches a
// lightweight task list so overdue/due-today reminder badges (and browser
// notifications) work no matter which module the user currently has open.
// `onToggleFocusMode` is optional and only passed by Dashboard.jsx - this
// bar is never itself hidden by any collapse toggle, so it's the one
// reliable place to always have a way back out of focus mode.
export function ModuleNavBar({ active, focusMode, onToggleFocusMode }) {
  const { t } = useTranslation();
  const { user, logout, logoutAll } = useAuth();
  const { reminderCount } = useTaskReminders();

  const handleLogoutAll = async () => {
    try {
      await logoutAll();
      toast.success(t("auth.signOutEverywhereSuccess"));
    } catch {
      toast.error(t("auth.signOutEverywhereFailedError"));
    }
  };
  const modules = [
    { key: "notes", path: "/dashboard", label: t("modules.notes"), Icon: NotePencil },
    { key: "tasks", path: "/tasks", label: t("modules.tasks"), Icon: CheckSquare, badge: reminderCount },
    { key: "schedule", path: "/schedule", label: t("modules.schedule"), Icon: CalendarBlank, badge: reminderCount },
  ];

  return (
    <nav className="flex items-center gap-0.5 border-b border-border bg-card px-1.5 py-1 md:px-2.5 md:py-1.5 shrink-0 overflow-x-auto" data-testid="module-nav-bar">
      {modules.map(({ key, path, label, Icon, badge }) => (
        <Link
          key={key}
          to={path}
          data-testid={`module-nav-${key}`}
          className={`relative flex items-center gap-1 px-1.5 py-1 md:gap-1.5 md:px-2.5 text-xs md:text-sm font-semibold whitespace-nowrap transition-colors ${
            active === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
          }`}
        >
          <Icon size={15} weight="bold" /> {label}
          {!!badge && (
            <span
              data-testid={`module-nav-${key}-reminder-badge`}
              className="ml-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none"
            >
              {badge}
            </span>
          )}
        </Link>
      ))}
      <div className="ml-auto flex items-center gap-1 md:gap-2 shrink-0">
        <GlobalSearchBox />
        {onToggleFocusMode && (
          <button
            type="button"
            data-testid="focus-mode-toggle-button"
            onClick={onToggleFocusMode}
            title={t(focusMode ? "modules.exitFocusMode" : "modules.focusMode")}
            className={`flex items-center gap-1.5 px-2 py-1 text-sm font-semibold whitespace-nowrap transition-colors ${
              focusMode ? "text-primary" : "text-muted-foreground hover:text-primary"
            }`}
          >
            {focusMode ? <ArrowsOut size={16} weight="bold" /> : <ArrowsIn size={16} weight="bold" />}
          </button>
        )}
        <OfflineStatusBadge />
        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="user-menu-button"
                className="flex items-center gap-1 pl-0.5 pr-1 py-0.5 text-[11px] md:text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
              >
                <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[11px] font-bold shrink-0">
                  {user.email?.[0]?.toUpperCase()}
                </span>
                <span className="hidden sm:inline max-w-[140px] truncate">{user.email}</span>
                <CaretDown size={10} weight="bold" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid="user-menu-content">
              {user.is_admin && (
                <DropdownMenuItem asChild data-testid="user-menu-admin-users" className="gap-2 cursor-pointer">
                  <Link to="/admin/users">
                    <UsersThree size={14} /> {t("admin.navLink")}
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="user-menu-logout" onClick={logout} className="gap-2 cursor-pointer">
                <SignOut size={14} /> {t("sidebar.logout")}
              </DropdownMenuItem>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <DropdownMenuItem
                    data-testid="user-menu-logout-all"
                    onSelect={(e) => e.preventDefault()}
                    className="gap-2 cursor-pointer text-destructive focus:text-destructive"
                  >
                    <SignOut size={14} weight="bold" /> {t("auth.signOutEverywhere")}
                  </DropdownMenuItem>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-none" data-testid="logout-all-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("auth.signOutEverywhereConfirmTitle")}</AlertDialogTitle>
                    <AlertDialogDescription>{t("auth.signOutEverywhereConfirmDescription")}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="logout-all-confirm-cancel" className="rounded-none">{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="logout-all-confirm-button"
                      className="rounded-none bg-destructive hover:bg-destructive/90"
                      onClick={handleLogoutAll}
                    >
                      {t("auth.signOutEverywhere")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </nav>
  );
}
